const SIZE_CLASSES = {
	sm: "h-8",
	md: "h-10 md:h-12",
	lg: "h-14 md:h-16",
} as const;

export function Logo({
	variant = "navy",
	size = "md",
	className = "",
}: {
	variant?: "navy" | "white";
	size?: keyof typeof SIZE_CLASSES;
	className?: string;
}) {
	const src =
		variant === "white"
			? "/logos/academy-lockup-white.svg"
			: "/logos/academy-lockup-navy.svg";

	return (
		<img
			src={src}
			alt="أكاديمية مستشير"
			width={646}
			height={168}
			className={`${SIZE_CLASSES[size]} w-auto object-contain ${className}`}
		/>
	);
}

export function LogoMark({ className = "h-9 w-auto" }: { className?: string }) {
	return (
		<img
			src="/logos/academy-icon.svg"
			alt="أكاديمية مستشير"
			width={47}
			height={43}
			className={className}
		/>
	);
}

/**
 * The icon mark (same artwork as /logos/academy-icon.svg) with its three
 * shapes as separate `<path>`s, each animated with a plain CSS `@keyframes`
 * (see src/styles.css) instead of a JS animation library — these are simple
 * opacity/transform loops, nothing a library buys us over native CSS.
 *
 * - "loop": continuous idle motion for a loading state — the two blocks
 *   breathe in and out of opacity out of phase, the diamond spins slowly.
 *   Reads as "still working" rather than "finished, just idling".
 * - "enter": a one-shot assembly animation for a page that only renders
 *   once, like the 404 — blocks slide in from either side, the diamond
 *   drops in and overshoots slightly before settling.
 */
export function AnimatedLogoMark({
	className = "h-16 w-auto",
	mode = "loop",
}: {
	className?: string;
	mode?: "loop" | "enter";
}) {
	const loop = mode === "loop";

	return (
		<svg
			viewBox="0 0 47 43"
			fill="none"
			className={className}
			xmlns="http://www.w3.org/2000/svg"
			role="img"
			aria-label="أكاديمية مستشير"
		>
			<path
				d="M23.1784 18.8657L16.2247 18.8672L11.5885 42.4485H0V0H11.5885L23.1784 18.8657Z"
				fill="#152C4C"
				style={
					loop
						? undefined
						: ({ "--logo-enter-x": "-8px" } as React.CSSProperties)
				}
				className={
					loop
						? "animate-logo-breathe"
						: "animate-[logo-enter-block_0.55s_ease-out_both]"
				}
			/>
			<path
				d="M23.1777 42.4491L28.9723 18.8662H40.5616L46.3562 42.4491H23.1777Z"
				fill="#17A6B1"
				style={
					loop
						? undefined
						: ({ "--logo-enter-x": "8px" } as React.CSSProperties)
				}
				className={
					loop
						? "animate-logo-breathe [animation-delay:0.4s]"
						: "animate-[logo-enter-block_0.55s_ease-out_0.12s_both]"
				}
			/>
			<path
				d="M34.7706 0L25.498 5.90497L34.7706 11.7914L44.0402 5.90497L34.7706 0Z"
				fill="#17A6B1"
				style={{ transformOrigin: "34.77px 5.9px" }}
				className={
					loop
						? "animate-logo-spin"
						: "animate-[logo-enter-diamond_0.6s_ease-out_0.25s_both]"
				}
			/>
		</svg>
	);
}
